import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '@templates/pursuitLeader/forecast/styles/styles.css';
import { MotifChip } from '@ey-xd/motif-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  EmptyForcastContainer,
  EmptyForcastText,
  TableData1C,
  TableData1CBold,
  PLPastStatusCell,
  PLPastTableContainer,
  PursuitTypeCell,
} from '@atoms/tableData/styles/tableData.styles'; //import table cells
import {
  pastMonthlyopportunitiesPL,
  pastQuaterlyopportunitiesPL,
} from '@services/pursuitLeaderServices'; //setup api call from here
import { getForecastSubmittedStatus } from '@services/commonServices';
import TableHeader from '@molecules/gcsp/components/tableHeader';
import SpinnerLoader from '@atoms/SpinnerLoader';
import { logError } from '@util/commonFunctions';
import wonIcon from '@assets/icons/medal.png';
import openIcon from '@assets/icons/dashed_circle.png';
import lostIcon from '@assets/icons/block_circle.png';
import {
  QUARTERLY_DETAILS,
  ABBREVIATED_MONTH_LIST,
  SOURCE,
  MONTHLY_QUARTERR,
} from '@constants/commonConstants';
import { isMobile } from 'react-device-detect';

import { columnDefs } from '@templates/pursuitLeader/forecast/tableComponent/plPastColumnDefs';
import { appInsights } from '../../../../../services/appInsightsService';

let gridApi;

const PursuitLeaderPastTable = props => {
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [totalPageRows, setTotalPageRows] = useState('12');
  const [hasSubmission, setHasSubmission] = useState(false);
  const forecast_type = props.forecast_type;
  const EMAIL = props.email;
  const MONTH = props.month;
  const QUARTER = props.quarter;
  const YEAR = props.year;
  const ROLE = 'PL';
  // let rowDataArray;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Calling past opportunities API with forecast_type: ${forecast_type}, EMAIL: ${EMAIL}, YEAR: ${YEAR}, QUARTER: ${QUARTER}, MONTH: ${MONTH}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 1, // 1 represents response log
        });
        const getOpportunitiesDetailsAPI = await (forecast_type === 'month'
          ? pastMonthlyopportunitiesPL(EMAIL, YEAR, QUARTER, MONTH)
          : forecast_type === 'quarter'
            ? pastQuaterlyopportunitiesPL(EMAIL, YEAR, QUARTER)
            : Promise.reject(new Error('Invalid forecast_type')));

        // rowDataArray = getOpportunitiesDetailsAPI.opportunities;
        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Response from past opportunities API is ${JSON.stringify(getOpportunitiesDetailsAPI)}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 1, // 1 represents response log
        });
        setRowData(getOpportunitiesDetailsAPI.opportunities);

        // Check if submission was made by calling the forecast submitted status API
        try {
          // For quarterly forecast, get the first month of the quarter
          let monthForPayload;
          if (forecast_type === 'quarter' && QUARTER) {
            const quarterKey = QUARTER.toLowerCase(); // e.g., 'q1', 'q2', 'q3', 'q4'
            const monthsInQuarter = MONTHLY_QUARTERR[quarterKey];
            if (monthsInQuarter && monthsInQuarter.length > 0) {
              monthForPayload = monthsInQuarter[0].toLowerCase(); // Get first month abbreviation (e.g., 'Jul' -> 'jul')
            }
          } else {
            // For monthly forecast, use the MONTH prop
            monthForPayload = MONTH.toLowerCase();
          }

          const submissionStatusPayload = {
            email: EMAIL,
            role: ROLE,
            month: monthForPayload,
            year: YEAR,
          };
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `Checking PL submission status for past forecast with payload ${JSON.stringify(submissionStatusPayload)}`,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 1,
          });
          const submissionStatus = await getForecastSubmittedStatus(submissionStatusPayload);
          // Check if monthly_submission is 'Submitted' (case insensitive)
          const wasSubmitted = submissionStatus?.monthly_submission?.toLowerCase() === 'submitted';
          setHasSubmission(wasSubmitted);
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `PL submission status response: ${JSON.stringify(submissionStatus)}, hasSubmission: ${wasSubmitted}`,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 1,
          });
        } catch (error) {
          appInsights.trackTrace({
            message: JSON.stringify({
              message: `Error checking PL submission status: ${error}`,
              userId: localStorage.getItem('userEmail'),
              source: SOURCE.FRONTEND,
            }),
            severityLevel: 3,
          });
          // If API fails, assume no submission to be safe
          setHasSubmission(false);
        }

        setTimeout(() => {
          const availableTableHeight = document.getElementById('container')?.clientHeight;
          const rowCount = availableTableHeight && calculatePageSize(availableTableHeight);
          setTotalPageRows(rowCount);
        }, 500);
      } catch (error) {
        logError('getSystemDateTime', error);
      } finally {
        const timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 2000);
        return () => clearTimeout(timeoutId); // eslint-disable-line
      }
    };

    fetchData();
  }, [forecast_type, EMAIL, YEAR, QUARTER, MONTH]);

  const calculatePageSize = tableHeight => {
    const screenHeight = window.innerHeight;
    const singleRowHeight = 43; //(in Pixel)
    const minusFactor = screenHeight <= 720 ? 1 : 2;
    const totalRows = Math.floor(tableHeight / singleRowHeight) - minusFactor;
    return JSON.stringify(totalRows);
  };

  const handleGridReady = params => {
    ({ api: gridApi } = params);
  };

  const TextCellRendererC = ({ value }) => <TableData1C>{value}</TableData1C>;
  TextCellRendererC.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const TextCellRendererCBold = ({ value }) => <TableData1CBold>{value}</TableData1CBold>;

  const PursuitTypeCellRenderer = ({ value }) => {
    // Convert pursuit types to uppercase(Brand New) for display
    const displayValue = value
      ? value
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : '';

    return <PursuitTypeCell>{displayValue}</PursuitTypeCell>;
  };
  PursuitTypeCellRenderer.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const CommitCellRenderer = ({ value }) => (
    <MotifChip
      variant={value === 0 ? 'disabled' : value === 1 ? 'success' : value === -1 ? 'error' : ''}
      type="dot"
    >
      {value === 0
        ? QUARTERLY_DETAILS.NOT_COMMITTED
        : value === 1
          ? QUARTERLY_DETAILS.COMMITTED
          : value === -1
            ? QUARTERLY_DETAILS.MISSED
            : ''}
    </MotifChip>
  );

  const StatusCellRenderer = ({ value }) => {
    if (value === 'open') {
      return (
        <div>
          <PLPastStatusCell>
            <img src={openIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.OPEN}</TableData1C>
          </PLPastStatusCell>
        </div>
      );
    } else if (value === 'won') {
      return (
        <div>
          <PLPastStatusCell>
            <img src={wonIcon} width={16} height={16} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.WON}</TableData1C>
          </PLPastStatusCell>
        </div>
      );
    } else if (value === 'declined') {
      return (
        <div>
          <PLPastStatusCell>
            <img src={lostIcon} width={16} height={16} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.DECLINED}</TableData1C>
          </PLPastStatusCell>
        </div>
      );
    } else {
      return (
        <div>
          <PLPastStatusCell>
            <img src={lostIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.LOST}</TableData1C>
          </PLPastStatusCell>
        </div>
      );
    }
  };

  const DateCellRenderer = props => {
    const date = props.date;
    const month = props.month;
    if (date === null) {
      return <TableData1C>-</TableData1C>;
    } else {
      const month_array = ABBREVIATED_MONTH_LIST;
      const isoDate = new Date(date);
      const derivedMonth = month_array[isoDate.getUTCMonth()];
      let derivedDate = isoDate.getUTCDate();
      const derivedYear = isoDate.getUTCFullYear();
      if (derivedDate < 10) {
        derivedDate = `0${derivedDate}`;
      }
      const final_date = `${derivedDate} ${derivedMonth} ${derivedYear} (${month})`;
      return <TableData1C>{final_date}</TableData1C>;
    }
  };

  const TextCellRendererD = props => {
    const commit_tov = props.commit_tov;
    const sold_tov = props.sold_tov;
    const tag = props.tag;
    // For addign '$' Sign to TOV value
    if (tag === 'won') {
      if (commit_tov === sold_tov) {
        const formattedValue = Number(commit_tov).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        return <TableData1C>{formattedValue}</TableData1C>;
      } else {
        const formattedValue1 = Number(sold_tov).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        const formattedValue2 = Number(commit_tov).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        return (
          <TableData1C>
            {formattedValue1} (<s>{formattedValue2}</s>)
          </TableData1C>
        );
      }
    } else {
      const formattedValue = Number(commit_tov).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return <TableData1C>{formattedValue}</TableData1C>;
    }
  };

  TextCellRendererD.propTypes = {
    value: PropTypes.string.isRequired, // eslint-disable-line
  };

  return (
    <PLPastTableContainer className="ag-theme-alpine">
      {isLoading ? (
        <div
          style={
            isMobile
              ? { position: 'fixed', top: '50%', left: '50%' }
              : { position: 'fixed', top: '50%', left: '35%' }
          }
        >
          <SpinnerLoader color="#155CB4" />
        </div>
      ) : (
        <>
          {rowData.length === 0 && !hasSubmission ? (
            <EmptyForcastContainer>
              <EmptyForcastText>
                You have missed the submission for this time period.
              </EmptyForcastText>
            </EmptyForcastContainer>
          ) : rowData.length === 0 && hasSubmission ? (
            // Blank submission - show nothing (empty screen)
            <div></div>
          ) : (
            <AgGridReact
              firstColumnBorderRight
              columnDefs={columnDefs}
              rowData={rowData}
              rowSelection="multiple"
              onGridReady={handleGridReady}
              suppressRowClickSelection
              suppressCellFocus
              suppressRowHoverHighlight={true}
              columnHoverHighlight={true}
              onSortChanged={() => gridApi.redrawRows()}
              components={{
                agColumnHeader: TableHeader, //for subtext add molecules/gcsp/components/tableHeader.jsx
                textCellRendererC: TextCellRendererC,
                statusCellRenderer: StatusCellRenderer,
                commitCellRenderer: CommitCellRenderer,
                textCellRendererCBold: TextCellRendererCBold,
                pursuitTypeCellRenderer: PursuitTypeCellRenderer,
                dateCellRenderer: DateCellRenderer,
                textCellRendererD: TextCellRendererD,
              }}
              alwaysShowHorizontalScroll={true}
              pagination={true}
              paginationPageSize={totalPageRows}
              paginationAutoPageSize={true}
            />
          )}
        </>
      )}
    </PLPastTableContainer>
  );
};
export default PursuitLeaderPastTable;
